import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Trash2, Download } from 'lucide-react';
import { comptabiliteApi } from '../../services/comptabiliteApi';
import type { CompteComptable, Journal, LigneEcriture } from '../../types/comptabilite';
import { formatCurrency } from '../../utils/formatters';
import { downloadFile } from '../../utils/download';

const num = (s: string | number | null | undefined) => Number(s ?? 0);

const JournalEcritures: React.FC = () => {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [journalFilter, setJournalFilter] = useState<number | ''>('');

  const { data: journaux = [] } = useQuery({ queryKey: ['compta', 'journaux'], queryFn: () => comptabiliteApi.getJournaux() });
  const { data: ecritures = [], isLoading } = useQuery({
    queryKey: ['compta', 'ecritures', journalFilter],
    queryFn: () => comptabiliteApi.getEcritures(journalFilter ? { journal: Number(journalFilter) } : undefined),
  });

  const refresh = () => queryClient.invalidateQueries(['compta']);

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <select value={journalFilter} onChange={(e) => setJournalFilter(e.target.value ? Number(e.target.value) : '')}
          className="border border-gray-200 rounded-xl px-3 py-2 text-gray-700">
          <option value="">Tous les journaux</option>
          {journaux.map((j) => <option key={j.id} value={j.id}>{j.code} — {j.libelle}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <button onClick={() => downloadFile('/comptabilite/rapports/export/', 'journal_general.xlsx', journalFilter ? { etat: 'journal', journal: Number(journalFilter) } : { etat: 'journal' })}
            className="inline-flex items-center gap-1.5 border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium px-3 py-2 rounded-lg">
            <Download className="h-4 w-4" /> Excel
          </button>
          <button onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium px-3 py-2 rounded-lg">
            <Plus className="h-4 w-4" /> Écriture manuelle (OD)
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="p-6 text-gray-500">Chargement des écritures…</div>
      ) : (
        <div className="space-y-3">
          {ecritures.map((e) => (
            <div key={e.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between bg-gray-50">
                <div className="text-sm">
                  <span className="font-mono text-gray-500">{e.numero}</span>
                  <span className="mx-2 text-gray-300">|</span>
                  <span className="text-gray-700">{e.libelle}</span>
                </div>
                <div className="text-xs text-gray-400">{e.date_ecriture} · {e.journal_code}
                  {!e.equilibree && <span className="ml-2 text-red-600 font-medium">déséquilibrée</span>}
                </div>
              </div>
              <table className="min-w-full text-sm">
                <tbody>
                  {e.lignes.map((l, i) => (
                    <tr key={i} className="border-b border-gray-50 last:border-0">
                      <td className="px-4 py-1.5 font-mono text-gray-500 w-24">{l.compte_numero}</td>
                      <td className="px-4 py-1.5 text-gray-700">{l.compte_intitule}</td>
                      <td className="px-4 py-1.5 text-right text-gray-700 w-32">{num(l.debit) ? formatCurrency(num(l.debit)) : ''}</td>
                      <td className="px-4 py-1.5 text-right text-gray-700 w-32">{num(l.credit) ? formatCurrency(num(l.credit)) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {ecritures.length === 0 && <div className="bg-white rounded-xl shadow-sm p-10 text-center text-gray-400">Aucune écriture.</div>}
        </div>
      )}

      {showForm && <EcritureForm journaux={journaux} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); refresh(); }} />}
    </div>
  );
};

const emptyLigne = (): LigneEcriture => ({ compte: 0, libelle: '', debit: 0, credit: 0 });

const EcritureForm: React.FC<{ journaux: Journal[]; onClose: () => void; onSaved: () => void }> = ({ journaux, onClose, onSaved }) => {
  const { data: comptes = [] } = useQuery({ queryKey: ['compta', 'comptes'], queryFn: () => comptabiliteApi.getComptes() });
  const odJournal = journaux.find((j) => j.code === 'OD') || journaux[0];
  const [journal, setJournal] = useState<number>(odJournal?.id ?? 0);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [libelle, setLibelle] = useState('');
  const [lignes, setLignes] = useState<LigneEcriture[]>([emptyLigne(), emptyLigne()]);

  const setLigne = (i: number, patch: Partial<LigneEcriture>) =>
    setLignes(lignes.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const totalD = lignes.reduce((s, l) => s + num(l.debit), 0);
  const totalC = lignes.reduce((s, l) => s + num(l.credit), 0);
  const equilibre = totalD === totalC && totalD > 0;
  const valid = equilibre && journal && libelle.trim() && lignes.every((l) => l.compte);

  const mutation = useMutation(
    () => comptabiliteApi.createEcriture({
      journal, date_ecriture: date, libelle,
      lignes: lignes.filter((l) => l.compte && (num(l.debit) || num(l.credit))),
    }),
    { onSuccess: onSaved },
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Nouvelle écriture</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <select value={journal} onChange={(e) => setJournal(Number(e.target.value))}
              className="border border-gray-200 rounded-xl px-3 py-2 text-gray-700">
              {journaux.map((j) => <option key={j.id} value={j.id}>{j.code}</option>)}
            </select>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-gray-700" />
            <input placeholder="Libellé" value={libelle} onChange={(e) => setLibelle(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-gray-700 col-span-1" />
          </div>

          <div className="space-y-2">
            {lignes.map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <select value={l.compte} onChange={(e) => setLigne(i, { compte: Number(e.target.value) })}
                  className="col-span-5 border border-gray-200 rounded-xl px-2 py-1.5 text-sm text-gray-700">
                  <option value={0}>— compte —</option>
                  {comptes.map((c: CompteComptable) => <option key={c.id} value={c.id}>{c.numero} {c.intitule}</option>)}
                </select>
                <input type="number" placeholder="Débit" value={l.debit || ''} onChange={(e) => setLigne(i, { debit: e.target.value, credit: 0 })}
                  className="col-span-3 border border-gray-200 rounded-xl px-2 py-1.5 text-sm text-gray-700" />
                <input type="number" placeholder="Crédit" value={l.credit || ''} onChange={(e) => setLigne(i, { credit: e.target.value, debit: 0 })}
                  className="col-span-3 border border-gray-200 rounded-xl px-2 py-1.5 text-sm text-gray-700" />
                <button onClick={() => setLignes(lignes.filter((_, idx) => idx !== i))}
                  className="col-span-1 text-gray-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
            <button onClick={() => setLignes([...lignes, emptyLigne()])}
              className="text-sm text-blue-600 hover:text-blue-800">+ Ajouter une ligne</button>
          </div>

          <div className={`text-sm font-medium ${equilibre ? 'text-green-600' : 'text-amber-600'}`}>
            Total débit {formatCurrency(totalD)} · Total crédit {formatCurrency(totalC)}
            {!equilibre && ' (déséquilibré)'}
          </div>
          {mutation.isError && <p className="text-sm text-red-600">Erreur lors de l'enregistrement.</p>}
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Annuler</button>
          <button disabled={!valid || mutation.isLoading} onClick={() => mutation.mutate()}
            className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg disabled:opacity-50">
            {mutation.isLoading ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JournalEcritures;
